import XAPI, { type Statement } from '@xapi/xapi'

import { stringifyPako, parsePako } from 'utils/gzip'

import { getAnalyticsConfig } from '../config/lms-config'
import { ScormSuspendData } from '../transport/types'
import type {
  CompletionStatus,
  ILmsEvent,
  IProviderConfig,
  IScore,
  LmsType,
  SuccessStatus,
} from '../types'
import { StatementBuilder } from '../utils/statement-builder'
import { BaseLmsProvider } from './base.provider'

const isAxiosErrorGuard = (
  err: unknown,
): err is { status: number; response?: { data?: { error?: string } } } =>
  typeof err === 'object' && err !== null && !Array.isArray(err)

export interface IXapiProviderOptions {
  useLocalStatements?: boolean
  bufferMaxSize?: number
  bufferFlushDelay?: number
}

export class XapiProvider extends BaseLmsProvider {
  readonly type: LmsType = 'xapi'

  protected lrs: XAPI | null = null
  protected statementBuilder: StatementBuilder | null = null
  protected options: IXapiProviderOptions

  private statementBuffer: Statement[] = []
  private flushTimeoutId: ReturnType<typeof setTimeout> | null = null
  private lastProgressValue: number | null = null

  constructor(options: IXapiProviderOptions = {}) {
    super()
    this.options = {
      useLocalStatements: false,
      bufferMaxSize: 10,
      bufferFlushDelay: 2000,
      ...options,
    }
  }

  async initialize(config: IProviderConfig): Promise<boolean> {
    if (!config.endpoint || !config.auth) {
      console.warn('[XapiProvider] Missing endpoint or auth')
      return false
    }

    this.config = config

    try {
      this.lrs = new XAPI({ endpoint: config.endpoint, auth: config.auth })
      this.statementBuilder = StatementBuilder.fromProviderConfig(config)
      this.connected = true

      this.setLearnerInfo({
        learnerName: config.actor.name || undefined,
        learnerEmail: config.actor.mbox?.replace('mailto:', '') || undefined,
        lmsType: 'xapi',
        activityId: config.activityId,
      })

      console.info('[XapiProvider] Connected to', config.endpoint)
      return true
    } catch (err) {
      console.error('[XapiProvider] Connection failed', err)
      return false
    }
  }

  async terminate(): Promise<void> {
    await this.flush()
    this.lrs = null
    this.config = null
    this.statementBuilder = null
    this.connected = false
    this.lastProgressValue = null
    console.info('[XapiProvider] Terminated')
  }

  async setSuspendData(data: unknown): Promise<void> {
    if (!this.lrs || !this.config) {
      return
    }

    try {
      await this.lrs.setState({
        agent: this.config.actor,
        activityId: this.config.activityId,
        stateId: 'suspend-data',
        state: stringifyPako(data),
      })
    } catch (err) {
      console.error('[XapiProvider] Error setting suspend data', err)
    }
  }

  async getSuspendData(): Promise<ScormSuspendData | null> {
    if (!this.lrs || !this.config) {
      return null
    }

    try {
      const res = await this.lrs.getState({
        agent: this.config.actor,
        activityId: this.config.activityId,
        stateId: 'suspend-data',
      })

      if (res?.data) {
        return parsePako(res.data as string)
      }
      return null
    } catch (err) {
      console.warn('[XapiProvider] Error getting suspend data', err)
      return null
    }
  }

  async setProgress(value: number): Promise<void> {
    // Skip if progress hasn't changed significantly (less than 1%)
    if (this.lastProgressValue !== null && Math.abs(value - this.lastProgressValue) < 1) {
      return
    }
    this.lastProgressValue = value

    // Round to 2 decimal places
    const roundedProgress = Math.round(value * 100) / 100
    const event: ILmsEvent = {
      verb: 'progressed',
      resultExtensions: {
        progress: roundedProgress,
        score: {
          raw: Math.round(value),
          min: 0,
          max: 100,
          scaled: Math.round((value / 100) * 10000) / 10000,
        },
      },
    }
    await this.trackEvent(event)
  }

  async setScore(score: IScore): Promise<void> {
    const event: ILmsEvent = {
      verb: 'scored',
      resultExtensions: {
        score: {
          raw: score.raw,
          min: score.min,
          max: score.max,
          scaled: score.scaled,
        },
      },
    }
    await this.trackEvent(event)
  }

  async setCompletionStatus(_status: CompletionStatus): Promise<void> {
    // xAPI: completion events are sent via trackEvent in handlers with full context
    // SCORM provider sets CMI values here, but xAPI relies on explicit trackEvent
  }

  async setSuccessStatus(_status: SuccessStatus): Promise<void> {
    // xAPI: success/failure events are sent via trackEvent in handlers with full context
    // SCORM provider sets CMI values here, but xAPI relies on explicit trackEvent
  }

  async trackEvent(event: ILmsEvent): Promise<void> {
    if (!this.lrs || !this.statementBuilder) {
      return
    }
    const isLocal = Boolean(this.options.useLocalStatements)
    const statement = this.statementBuilder.buildStatement(event, isLocal)

    this.queueStatement(statement)
  }

  public sendAnalyticsViaBeacon(events: ILmsEvent[]): boolean {
    const statements: Statement[] = events.map((event) => {
      if (!this.statementBuilder) {
        throw new Error('StatementBuilder not initialized')
      }
      const isLocal = Boolean(this.options.useLocalStatements)
      return this.statementBuilder.buildStatement(event, isLocal)
    })
    if (!this.config || statements.length === 0) {
      console.warn('[XapiProvider] Cannot send analytics - no config or empty statements')
      return false
    }

    const endpoint = `${this.config.endpoint!}/statements`
    const auth = this.config.auth!

    const payload = JSON.stringify(statements)

    try {
      if (typeof fetch !== 'undefined') {
        fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: auth,
            'X-Experience-API-Version': '1.0.3',
          },
          body: payload,
          keepalive: true,
        })
          .then(() => {
            console.info('[XapiProvider] ✓ Statements delivered via fetch keepalive')
            return true
          })
          .catch((err) => {
            console.error('[XapiProvider] Fetch keepalive error:', err)
          })

        console.info('[XapiProvider] ✓ Sent via fetch keepalive')
        return true
      }
    } catch (err) {
      console.warn('[XapiProvider] fetch not available, falling back to XHR:', err)
    }

    try {
      // const blob = new Blob([payload], { type: 'application/json' })

      // if (navigator.sendBeacon) {
      //   const endpointWithAuth = `${endpoint}?auth=${encodeURIComponent(auth)}`
      //   const success = navigator.sendBeacon(endpointWithAuth, blob)
      //   console.info(`[XapiProvider] Sent ${statements.length} statements via beacon:`, success)
      //   return success
      // } else {
      // Fallback  sync XHR
      const xhr = new XMLHttpRequest()
      xhr.open('POST', endpoint, false) // false = synchronous
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.setRequestHeader('Authorization', auth)
      xhr.send(payload)
      const success = xhr.status >= 200 && xhr.status < 300
      console.info(`[XapiProvider] Sent ${statements.length} statements via XHR:`, success)
      return success
      // }
    } catch (err) {
      console.error('[XapiProvider] Error sending analytics via beacon', err)
      return false
    }
  }

  async commit(): Promise<void> {
    await this.flush()
  }

  protected queueStatement(statement: Statement): void {
    if (!this.lrs) {
      return
    }

    this.statementBuffer.push(statement)

    if (this.statementBuffer.length >= (this.options.bufferMaxSize || 10)) {
      void this.flush()
      return
    }

    if (!this.flushTimeoutId) {
      this.flushTimeoutId = setTimeout(() => {
        void this.flush()
      }, this.options.bufferFlushDelay || 2000)
    }
  }

  protected async flush(): Promise<void> {
    if (this.flushTimeoutId) {
      clearTimeout(this.flushTimeoutId)
      this.flushTimeoutId = null
    }

    if (this.statementBuffer.length === 0 || !this.lrs) {
      return
    }

    const statements = [...this.statementBuffer]
    this.statementBuffer = []

    try {
      await this.lrs.sendStatements({ statements })
    } catch (err) {
      console.error('[XapiProvider] Error sending statements', err)
      const analyticsConfig = getAnalyticsConfig()
      const isLocal = Boolean(this.options.useLocalStatements)
      const isUnauthorized =
        isAxiosErrorGuard(err) &&
        (err.status === 401 || err.response?.data?.error === 'INVALID_OR_EXPIRED_TOKEN')
      if (isLocal && this.config?.fetchLrsConfig && analyticsConfig && isUnauthorized) {
        try {
          const newConfig = await this.config.fetchLrsConfig(analyticsConfig)
          if (newConfig?.auth && newConfig?.endpoint) {
            this.config = { ...this.config, auth: newConfig.auth, endpoint: newConfig.endpoint }
            this.lrs = new XAPI({ endpoint: newConfig.endpoint, auth: newConfig.auth })
            this.statementBuffer.unshift(...statements)
          }
        } catch (refreshErr) {
          console.error('[XapiProvider] Failed to refresh LRS config', refreshErr)
        }
      }
    }
  }
}
